import type { ComponentType, CSSProperties } from "react";
import { GlobeIcon } from "@radix-ui/react-icons";

export interface ReviewSourceIconComponentProps {
  className?: string;
  style?: CSSProperties;
  title?: string;
}

interface ReviewSourceIconComponentResult {
  kind: "component";
  key: string;
  Component: ComponentType<ReviewSourceIconComponentProps>;
  lockColor: boolean;
}

interface ReviewSourceIconAssetResult {
  kind: "asset";
  key: string;
  src: string;
}

export type ReviewSourceIconResult = ReviewSourceIconComponentResult | ReviewSourceIconAssetResult;

interface ReviewSourceIconOptions {
  variant?: string | null;
}

interface ReviewSourceVariantDefinition {
  key: string;
  label: string;
  asset: string;
  aliases?: string[];
  lockColor?: boolean;
}

interface ReviewSourceDefinition {
  defaultKey: string;
  variants: ReviewSourceVariantDefinition[];
}

interface VariantLookupRecord {
  provider: string;
  entry: ReviewSourceVariantDefinition;
}

const REVIEW_SOURCE_DEFINITIONS: Record<string, ReviewSourceDefinition> = {
  google: {
    defaultKey: "google-g",
    variants: [
      {
        key: "google-g",
        label: "Google · Monogram",
        asset: "/review-logos/Google__G__logo.svg",
        aliases: ["google"],
      },
      {
        key: "google-wordmark",
        label: "Google · Wordmark",
        asset: "/review-logos/google.svg",
      },
      {
        key: "google-favicon",
        label: "Google · Gradient icon",
        asset: "/review-logos/Google_Favicon_2025.svg",
      },
      {
        key: "google-maps",
        label: "Google Maps",
        asset: "/review-logos/google-maps.png",
        aliases: ["google maps", "maps"],
      },
    ],
  },
  facebook: {
    defaultKey: "facebook",
    variants: [
      {
        key: "facebook",
        label: "Facebook · Icon",
        asset: "/review-logos/facebook.png",
        aliases: ["facebook-icon"],
      },
      {
        key: "facebook-wordmark",
        label: "Facebook · Wordmark",
        asset: "/review-logos/facebook.svg",
      },
    ],
  },
  instagram: {
    defaultKey: "instagram",
    variants: [
      {
        key: "instagram",
        label: "Instagram · Glyph",
        asset: "/review-logos/instagram.png",
      },
      {
        key: "instagram-wordmark",
        label: "Instagram · Wordmark",
        asset: "/review-logos/instagram.svg",
      },
    ],
  },
  yelp: {
    defaultKey: "yelp",
    variants: [
      {
        key: "yelp",
        label: "Yelp",
        asset: "/review-logos/yelp.svg",
      },
    ],
  },
  tripadvisor: {
    defaultKey: "tripadvisor",
    variants: [
      {
        key: "tripadvisor",
        label: "Tripadvisor",
        asset: "/review-logos/tripadvisor.svg",
      },
    ],
  },
  trustpilot: {
    defaultKey: "trustpilot",
    variants: [
      {
        key: "trustpilot",
        label: "Trustpilot",
        asset: "/review-logos/trustpilot.svg",
      },
    ],
  },
};

const VARIANT_LOOKUP = new Map<string, VariantLookupRecord>();

const PROVIDER_KEYS = Object.keys(REVIEW_SOURCE_DEFINITIONS);

const registerVariant = (provider: string, variant: ReviewSourceVariantDefinition) => {
  const canonical = variant.key.trim().toLowerCase();
  if (!canonical) return;
  const record: VariantLookupRecord = { provider, entry: variant };
  VARIANT_LOOKUP.set(canonical, record);

  for (const alias of variant.aliases ?? []) {
    const normalizedAlias = alias.trim().toLowerCase();
    if (normalizedAlias.length === 0) continue;
    VARIANT_LOOKUP.set(normalizedAlias, record);
  }
};

Object.entries(REVIEW_SOURCE_DEFINITIONS).forEach(([provider, definition]) => {
  definition.variants.forEach((variant) => registerVariant(provider, variant));
});

function matchProviderForCandidate(candidate?: string | null): string | undefined {
  if (!candidate) return undefined;
  const normalized = candidate.trim().toLowerCase();
  if (!normalized || normalized === "auto" || normalized === "default") {
    return undefined;
  }

  const variantRecord = VARIANT_LOOKUP.get(normalized);
  if (variantRecord) {
    return variantRecord.provider;
  }

  for (const provider of PROVIDER_KEYS) {
    if (normalized === provider || normalized.includes(provider)) {
      return provider;
    }
  }

  return undefined;
}

const DEFAULT_VARIANT_BY_PROVIDER = new Map<string, VariantLookupRecord>();

Object.entries(REVIEW_SOURCE_DEFINITIONS).forEach(([provider, definition]) => {
  const defaultRecord = (() => {
    const normalized = definition.defaultKey.trim().toLowerCase();
    return normalized.length > 0 ? VARIANT_LOOKUP.get(normalized) : undefined;
  })();
  if (defaultRecord) {
    DEFAULT_VARIANT_BY_PROVIDER.set(provider, defaultRecord);
  }
});

export const REVIEW_SOURCE_VARIANTS: Record<string, Array<{ key: string; label: string }>> =
  Object.fromEntries(
    Object.entries(REVIEW_SOURCE_DEFINITIONS).map(([provider, definition]) => [
      provider,
      definition.variants.map(({ key, label }) => ({ key, label })),
    ]),
  );

const FALLBACK_COMPONENT: ReviewSourceIconComponentResult = {
  kind: "component",
  key: "fallback",
  lockColor: false,
  Component: ({ className, style, title }) => {
    const { width, height, color, ...restStyle } = style ?? {};
    const resolvedWidth = width ?? "100%";
    const resolvedHeight = height ?? "100%";
    const resolvedColor = typeof color === "string" ? color : undefined;
    const containerClass = className ?? "inline-flex items-center justify-center";

    return (
      <span
        className={containerClass}
        style={{ ...restStyle, width: resolvedWidth, height: resolvedHeight, color: resolvedColor }}
        role="img"
        aria-hidden
        title={title}
      >
        <GlobeIcon className="h-full w-full" />
      </span>
    );
  },
};

function resolveVariantRecord(variantKey?: string | null): VariantLookupRecord | undefined {
  if (!variantKey) return undefined;
  const normalized = variantKey.trim().toLowerCase();
  if (normalized.length === 0 || normalized === "auto" || normalized === "default") {
    return undefined;
  }
  return VARIANT_LOOKUP.get(normalized);
}

export function canonicalizeReviewSourceVariantKey(variantKey?: string | null): string | undefined {
  return resolveVariantRecord(variantKey)?.entry.key;
}

const findDefaultVariantForCandidate = (candidate: string): VariantLookupRecord | undefined => {
  const provider = matchProviderForCandidate(candidate);
  return provider ? DEFAULT_VARIANT_BY_PROVIDER.get(provider) : undefined;
};

export function resolveReviewSourceProviderKey(candidate?: string | null): string | undefined {
  return matchProviderForCandidate(candidate);
}

export function getReviewSourceIcon(
  sourceId?: string | null,
  fallbackName?: string | null,
  options?: ReviewSourceIconOptions,
): ReviewSourceIconResult | null {
  const requestedVariant = resolveVariantRecord(options?.variant);
  if (requestedVariant) {
    return {
      kind: "asset",
      key: requestedVariant.entry.key,
      src: requestedVariant.entry.asset,
    };
  }

  const candidates = [sourceId, fallbackName]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.trim().toLowerCase());

  for (const candidate of candidates) {
    const directVariant = VARIANT_LOOKUP.get(candidate);
    if (directVariant) {
      return {
        kind: "asset",
        key: directVariant.entry.key,
        src: directVariant.entry.asset,
      };
    }

    const defaultVariant = findDefaultVariantForCandidate(candidate);
    if (defaultVariant) {
      return {
        kind: "asset",
        key: defaultVariant.entry.key,
        src: defaultVariant.entry.asset,
      };
    }
  }

  return FALLBACK_COMPONENT;
}
